import { useEffect, useMemo, useRef, useState } from "react";
import * as styles from "./styles.module.scss"; // Adjust the path as necessary
import clsx from "../../../modules/helpers/clsx";

export type ResponsiveSize = [number | null, number | null, number][];

interface IProps {
  src: string;
  name?: string;
  lazyLoad?: boolean;
  imageType?: "portrait" | "landscape" | "full_land" | "square" | "original";
  sizes?: ResponsiveSize;
  noBlurPlaceholder?: boolean;
}

const ImageGenerator = (props: IProps) => {
  const [compressedSrc, setCompressedSrc] = useState<string>("");
  const [blurStyle, setBlurStyle] = useState<any>();
  const [fadeImage, setFadeImage] = useState(true);
  const [shouldRenderImage, setShouldRenderImage] = useState(false);
  const imageRef = useRef<HTMLPictureElement>(null);

  const extensionPreset = useMemo(() => {
    const extension = props.src?.split(".").at(-1);
    if (["jpg", "jpeg"].includes(extension)) {
      return "fjpeg";
    }
    return "fpng";
  }, [props.src]);

  useEffect(() => {
    if (props.src) {
      if (shouldRenderImage) {
        setCompressedSrc(props.src + `?imgProcess=pr:${extensionPreset}:w${props.sizes[0][2]}`);
      }
      if (extensionPreset === "fjpeg" && props.lazyLoad && window.IntersectionObserver && shouldRenderImage) {
        setBlurStyle({
          backgroundImage: `url('${props.src}?imgProcess=pr:${extensionPreset}:w100:bl10')`,
        });
      }
    }
  }, [props.sizes, props.src, shouldRenderImage]);

  useEffect(() => {
    if (props.lazyLoad && window.IntersectionObserver) {
      const options = {
        root: document.getElementById("root"),
        rootMargin: "0px",
        threshold: 0.2,
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setShouldRenderImage(true);
        }
      }, options);

      observer.observe(imageRef.current);

      return () => {
        observer.unobserve(imageRef.current);
      };
    } else {
      setShouldRenderImage(true);
    }
  }, []);

  return (
    <picture
      ref={imageRef}
      style={blurStyle}
      className={clsx({
        [styles.picture]: true,
        [styles.landscape]: props.imageType === "landscape",
        [styles.portrait]: props.imageType === "portrait",
        [styles.full_land]: props.imageType === "full_land",
        [styles.square]: props.imageType === "square",
        [styles.original]: props.imageType === "original" || !props.imageType,
      })}
    >
      <img
        loading={props.lazyLoad ? "lazy" : "eager"}
        onLoad={() => setFadeImage(false)}
        className={clsx({
          [styles.imageEl]: true,
          [styles.faded]: fadeImage,
        })}
        src={compressedSrc}
      />
    </picture>
  );
};

export default ImageGenerator;
